// 캐릭터 시스템 진입점(docs/CHARACTER-PRD.md §13). main.ts는 createGame 전에 bootCharacters() 한 줄만 부른다.
// UI 레인(캐릭터 선택·생성 화면)이 쓰는 함수: listCharacters · currentCharacter · checkCharacterName · createCharacter · selectCharacter ·
//   renameCharacter · deleteCharacter · restoreCharacter · refreshCharacters. 캐릭터 바꾸기 = selectCharacter → 게임 저장 flush → 새로고침.
//
// 켜기: CHARACTERS_ENABLED(UI가 준비되면 UI 레인이 true로) 또는 주소 ?chars=1(개발·QA). 꺼져 있으면 지금 빌드와 완전히 같다(원래 키).
import { applyStagedRestore, type ApplyResult } from '../account/sync';
import { ACCOUNT_META_KEY, COPY_SUFFIXES, SYNC_KEYS, collectSnapshot, type KV } from '../account/keys';
import { PLAYABLE_HEROES } from '../shared/types';
import { HERO_SELECT_KEY } from '../game/heroSelect';
import { TransportError } from '../account/sync';
import { CHAR_KEY_PREFIX, MIGRATION_STAGING_PREFIX, CharacterStorage, charKey, charSyncKeys, isInstalling } from './keys';
import { activateCharacter, activeCharacterId } from './scope';
import { installVerified, legacyHashOf } from './online';
import { hasLegacyProgress, legacyLevels } from './migrate';
import { fromView, loadRoster, mergeServerList, saveRoster, ROSTER_KEY, type LocalCharacter, type Roster } from './roster';
import { checkName, nameReasonText, type NameCheck } from './names';
import { characterTransport, fetchCharacterApi, type CharacterApi } from './api';
import { freshMeta, loadMeta, saveMeta, type Transport } from '../account/sync';
import { EMPTY_SERVER_SNAPSHOT, snapshotHash, writeSnapshot } from '../account/keys';
import { CLASS_IDS, CLASS_LABEL, LOOK_COUNT, SLOT_BASE, SLOT_MAX, formatHandle, isClassId, type CharacterView, type ClassId, type NameReason, type SlotView } from './protocol';
import { previewQueryAllowedHere } from '../shared/previewFlags';

export { CLASS_IDS, CLASS_LABEL, LOOK_COUNT, SLOT_BASE, SLOT_MAX, formatHandle, nameReasonText, activeCharacterId, hasLegacyProgress };
export type { ClassId, CharacterView, LocalCharacter, NameCheck, NameReason, SlotView };

/** 캐릭터 기능 스위치. UI(선택·생성 화면)가 붙기 전에는 false — 켜면 기존 유저 이관이 첫 부팅에 일어난다 */
export const CHARACTERS_ENABLED = false;

export type BootMode =
  /** 꺼짐: 원래 키(지금 빌드와 같다) */
  | 'off'
  /** 켜졌지만 원래 키로 간다(저장소 막힘·목록 손상·이관 쓰기 실패 — 다음 부팅에 다시 시도) */
  | 'legacy'
  /** 지금 캐릭터가 없다: UI가 선택·생성 화면을 먼저 보여야 한다(이 상태에서 게임을 진행시키지 않는다 — 원래 키에 쓰게 된다) */
  | 'select'
  /** 지금 캐릭터로 시작 */
  | 'active';
export interface BootResult {
  mode: BootMode;
  characterId: string | null;
  /** 이번 부팅에 이관했는가(UI가 "이름을 정해 주세요" 안내를 띄울 근거) */
  migrated: boolean;
  /** 캐릭터 스냅샷 대기분 적용 결과(다른 기기 진행 불러오기) */
  restore: ApplyResult | null;
  reason?: string;
}
export interface BootOptions {
  storage?: KV | null;
  enabled?: boolean;
  /** 계정 클라이언트가 알던 계정 id(이관 캐릭터 id 씨앗). 없으면 계정 메타에서 읽는다 */
  accountId?: string | null;
  /** 계정 삭제 + 이 기기 진행 지우기가 방금 적용됐다(account applyStagedRestore → wiped): 캐릭터 키·목록도 지운다 */
  wiped?: boolean;
  now?: () => number;
  randomHex?: (bytes: number) => string;
}

function browserStorage(): KV | null {
  try { const s = window.localStorage; s.getItem(ROSTER_KEY); return s; } catch { return null; }
}
function randomHex(bytes: number): string {
  const a = new Uint8Array(bytes);
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) crypto.getRandomValues(a);
  else for (let i = 0; i < bytes; i++) a[i] = Math.floor(Math.random() * 256);
  return [...a].map((b) => b.toString(16).padStart(2, '0')).join('');
}
/** 켜짐 = 스위치 또는 ?chars=1. 쿼리는 운영 호스트(*.tllhouse.com)에서 무시한다 — 실제 계정에 이관·삭제가 일어나지 않게(#234 previewFlags와 같은 규칙) */
export function charactersEnabled(search = typeof location !== 'undefined' ? location.search : '', allowPreview = previewQueryAllowedHere()): boolean {
  return CHARACTERS_ENABLED || (allowPreview && new URLSearchParams(search).get('chars') === '1');
}
function knownAccountId(kv: KV): string | null {
  try { const m = JSON.parse(kv.getItem(ACCOUNT_META_KEY) ?? 'null') as { accountId?: unknown } | null; return m && typeof m.accountId === 'string' ? m.accountId : null; } catch { return null; }
}

/** 한 캐릭터의 로컬 키 전부 지우기(진행 키·사본·동기화 키). 계정 삭제 + 기기 진행 지우기 전용 */
function wipeCharacterKeys(kv: KV, id: string): void {
  for (const k of SYNC_KEYS) for (const s of ['', ...COPY_SUFFIXES]) kv.removeItem(charKey(id, k + s));
  const sk = charSyncKeys(id);
  kv.removeItem(sk.meta); kv.removeItem(sk.apply); kv.removeItem(sk.backup);
}

/** 저장소의 모든 키(localStorage·시험 저장소처럼 length·key(i)가 있으면). 열거할 수 없으면 null */
function storageKeys(kv: KV): string[] | null {
  const s = kv as unknown as { length?: unknown; key?: (i: number) => string | null };
  if (typeof s.length !== 'number' || typeof s.key !== 'function') return null;
  const out: string[] = [];
  for (let i = 0; i < s.length; i++) { const k = s.key(i); if (k !== null) out.push(k); }
  return out;
}
/**
 * 계정 삭제 + 기기 진행 지우기: 저장소에서 캐릭터 이름공간(dungeonhero.char.)을 직접 훑어 전부 지운다 — 목록에서 이미 빠진 캐릭터(영구 삭제)도(Codex P2 #214 3차).
 * 열거할 수 없는 저장소면 목록의 캐릭터만. 그다음 목록 삭제
 */
export function wipeAllCharacters(kv: KV): void {
  try {
    const all = storageKeys(kv);
    if (all) { for (const k of all) if (k.startsWith(CHAR_KEY_PREFIX) || k.startsWith(MIGRATION_STAGING_PREFIX)) kv.removeItem(k); }
    else {
      const old = loadRoster(kv);
      if (old && old !== 'corrupt') for (const c of old.characters) wipeCharacterKeys(kv, c.id);
    }
    kv.removeItem(ROSTER_KEY);
  } catch { /* 지우기 실패: 다음 계정 삭제에서 다시 */ }
}

/**
 * 부팅 맨 처음(createGame 전, account applyStagedRestore 뒤): 목록 읽기 → (처음이면) 이관 → 지금 캐릭터 활성화 → 그 캐릭터의 서버 진행 대기분 적용.
 * 어떤 실패도 던지지 않는다(원래 키로 간다).
 */
export function bootCharacters(o: BootOptions = {}): BootResult {
  const off = (mode: BootMode, reason?: string): BootResult => { activateCharacter(null); return { mode, characterId: null, migrated: false, restore: null, reason }; };
  const enabled = o.enabled ?? charactersEnabled();
  // 계정 삭제 + 기기 진행 지우기는 스위치와 상관없이 처리한다(꺼진 부팅에서 지우기 표시가 소비되면 캐릭터 진행이 영영 남는다 — Codex P2 #214)
  if (o.wiped) {
    const wkv = o.storage === undefined ? browserStorage() : o.storage;
    if (wkv) wipeAllCharacters(wkv);
  }
  if (!enabled) return off('off');
  const kv = o.storage === undefined ? browserStorage() : o.storage;
  if (!kv) return off('legacy', 'storage');
  const now = o.now ?? (() => Date.now());
  try {
    const loaded = loadRoster(kv);
    if (loaded === 'corrupt') return off('legacy', 'roster-corrupt');
    // 이관은 부팅이 하지 않는다(계정당 한 번, 서버 기준 — online.ts). 처리 표시가 없으면 원래 키로 플레이(지금과 같다)
    if (!loaded || !loaded.migrated) return off('legacy', 'migration-pending');
    const roster: Roster = deriveRosterState(kv, loaded);
    const migrated = false;
    const id = roster.current;
    const curRow = id ? roster.characters.find((c) => c.id === id) : undefined;
    // 삭제 대기 캐릭터로는 시작하지 않는다(서버가 저장을 거절한다) — 선택 화면으로
    if (!id || !curRow || curRow.deletedAt || curRow.needsDownload) return { ...off('select'), migrated };
    activateCharacter(id);
    // 새로 만든 캐릭터는 영웅 선택 키가 없다: 직업으로 채운다(없으면 HeroSelectStore가 카엘로 시작한다 — Codex P2 #214). 플레이 가능 영웅만
    initHeroSelect(kv, id, roster.characters.find((c) => c.id === id)?.cls);
    const restore = applyStagedRestore(new CharacterStorage(kv, id), now, charSyncKeys(id));
    return { mode: 'active', characterId: id, migrated, restore };
  } catch { return off('legacy', 'error'); }
}

/** 이관(처리) 뒤 원래 키에 진행이 더 쌓였는가(옛 빌드에서 더 플레이) — 캐릭터 화면 안내 한 줄 「옛 진행이 따로 보관돼 있어요」의 근거. 원래 키는 지우지 않는다 */
export function legacyProgressAfterMigration(kv: KV | null = browserStorage()): boolean {
  if (!kv) return false;
  const r = loadRoster(kv);
  if (!r || r === 'corrupt' || !r.migrated) return false;
  const now = legacyHashOf(kv);
  return now !== null && now !== r.migrated.legacyHash;
}

// ── UI가 쓰는 함수 ──

let api: CharacterApi | null = null;
function charApi(): CharacterApi { return (api ??= fetchCharacterApi()); }
/** 시험·QA용 주입 */
export function setCharacterApi(a: CharacterApi | null): void { api = a; }

function rosterOf(kv: KV | null): Roster | null {
  if (!kv) return null;
  const r = loadRoster(kv);
  return r && r !== 'corrupt' ? deriveRosterState(kv, r) : null;
}

/**
 * 목록 파생 상태 — 「받는 중」(needsDownload) 판정은 여기 한 곳뿐이다(목록 맞추기·이관 완료·병합 뒤·부팅이 모두 이 함수를 거친다, Codex P2 #214).
 * 규칙: 등록된 캐릭터의 서버 rev가 0보다 크고, 로컬에 설치·검증된 기준(sync base)이 없거나 그 rev가 서버 rev보다 뒤처졌으면서 로컬이 기준 뒤로 바뀌지 않았으면 받는 중.
 * (로컬이 기준 뒤로 바뀌었으면 받지 않고 동기화 엔진의 충돌 판정에 맡긴다 — 새 로컬 진행을 버리지 않게.) 받는 중·삭제 대기 캐릭터는 지금 캐릭터가 될 수 없다
 */
export function deriveRosterState(kv: KV, r: Roster): Roster {
  const characters = r.characters.map((c): LocalCharacter => {
    const { needsDownload: _old, ...rest } = c;
    // 설치가 끝나지 않았으면(표시가 남음 — 쓰기 실패·탭 종료) 무조건 받는 중
    if (isInstalling(kv, c.id)) return { ...rest, needsDownload: true };
    if (!c.registered || !(c.serverRev && c.serverRev > 0)) return rest;
    let base: { rev: number; hash: string } | null = null;
    try { base = loadMeta(kv, charSyncKeys(c.id).meta).base; } catch { base = null; }
    if (!base) return { ...rest, needsDownload: true };
    if (base.rev >= c.serverRev) return rest; // 로컬 기준이 서버와 같거나 앞섬(이 기기가 올린 뒤 목록이 옛 rev) — 받을 것 없음
    let localHash: string | null = null;
    try { const snap = collectSnapshot(new CharacterStorage(kv, c.id)); localHash = snap ? snapshotHash(snap) : null; } catch { localHash = null; }
    return localHash === base.hash ? { ...rest, needsDownload: true } : rest;
  });
  const current = r.current && characters.some((c) => c.id === r.current && !c.deletedAt && !c.needsDownload) ? r.current : null;
  return { ...r, characters, current };
}

/** 로컬 목록(서버와 맞춘 마지막 사본). 슬롯 순 */
export function listCharacters(kv: KV | null = browserStorage()): LocalCharacter[] { return rosterOf(kv)?.characters ?? []; }
/** 지금 캐릭터 줄(없으면 null) */
export function currentCharacter(kv: KV | null = browserStorage()): LocalCharacter | null {
  const r = rosterOf(kv);
  return r?.characters.find((c) => c.id === r.current) ?? null;
}
/** 이름 실시간 형식 검사(생성 버튼을 누르면 서버가 같은 규칙으로 다시 본다) */
export function checkCharacterName(name: string): NameCheck { return checkName(name); }

/** 실패 사유(UI 문구 고르기) */
export type CharacterFailure =
  | { code: 'offline' } // 서버에 닿지 않음(생성·이름은 태그 발급 때문에 막는다)
  | { code: 'bad-name'; reason: NameReason }
  | { code: 'slots-full' | 'name-full' | 'rename-locked' | 'character-deleted' | 'not-restorable' | 'id-taken' | 'not-found' | 'no-session' | 'rate' | 'storage' | 'error'; detail?: string };
export type CharacterOutcome<T> = { ok: true; value: T } | { ok: false; failure: CharacterFailure };

function failure(err: unknown): CharacterFailure {
  if (!(err instanceof TransportError)) return { code: 'error' };
  if (err.status === 0 || err.status >= 500) return { code: 'offline' };
  const [code, reason] = err.code.split(':');
  if (code === 'bad-name') return { code: 'bad-name', reason: (reason as NameReason) ?? 'charset' };
  if (code === 'rate' || err.status === 429) return { code: 'rate' };
  const known = ['slots-full', 'name-full', 'rename-locked', 'character-deleted', 'not-restorable', 'id-taken', 'not-found', 'no-session'] as const;
  return (known as readonly string[]).includes(code) ? { code: code as (typeof known)[number] } : { code: 'error', detail: err.code };
}
function updateRoster(kv: KV, f: (r: Roster) => Roster): boolean {
  const r = rosterOf(kv);
  return !!r && saveRoster(kv, f(r));
}

/** 서버 목록으로 로컬 사본을 맞춘다(선택 화면을 열 때) */
export async function refreshCharacters(kv: KV | null = browserStorage()): Promise<CharacterOutcome<{ characters: LocalCharacter[]; slots: SlotView }>> {
  if (!kv) return { ok: false, failure: { code: 'storage' } };
  try {
    const res = await charApi().list();
    const free = res.characters.length < res.slots.cap;
    if (!updateRoster(kv, (r) => { const m = mergeServerList(r, res.characters); return free ? { ...m, characters: m.characters.map((x) => (x.localOnly ? { ...x, localOnly: undefined } : x)) } : m; })) return { ok: false, failure: { code: 'storage' } };
    await hydrateCharacters(kv, accountOf(kv));
    return { ok: true, value: { characters: listCharacters(kv), slots: res.slots } };
  } catch (err) { return { ok: false, failure: failure(err) }; }
}

/** 새 캐릭터(서버가 이름#태그를 발급 — 오프라인이면 offline). 성공하면 목록에 넣는다(선택은 selectCharacter) */
export async function createCharacter(input: { cls: ClassId; look: number; name: string }, kv: KV | null = browserStorage()): Promise<CharacterOutcome<LocalCharacter>> {
  if (!kv) return { ok: false, failure: { code: 'storage' } };
  if (!isClassId(input.cls)) return { ok: false, failure: { code: 'error', detail: 'cls' } };
  const c = checkName(input.name);
  if (!c.ok) return { ok: false, failure: { code: 'bad-name', reason: c.reason } };
  // 응답을 못 받은 같은 내용의 요청이 있으면 그 id로 다시 보낸다(서버가 처음 만든 캐릭터를 돌려준다 — 슬롯·이름을 두 번 쓰지 않게)
  const pending = rosterOf(kv)?.pendingCreate;
  const id = pending && pending.cls === input.cls && pending.look === input.look && pending.name === c.name ? pending.id : 'c_' + randomHex(12);
  if (!updateRoster(kv, (r) => ({ ...r, pendingCreate: { id, cls: input.cls, look: input.look, name: c.name } }))) return { ok: false, failure: { code: 'storage' } };
  const clearPending = (r: Roster): Roster => { const { pendingCreate: _p, ...rest } = r; return rest; };
  try {
    const res = await charApi().create({ id, cls: input.cls, look: input.look, name: c.name });
    const local = fromView(res.character);
    if (!updateRoster(kv, (r) => ({ ...clearPending(r), characters: [...r.characters.filter((x) => x.id !== local.id), local].sort((a, b) => a.slot - b.slot) }))) return { ok: false, failure: { code: 'storage' } };
    initHeroSelect(kv, local.id, local.cls); // 만든 직업으로 바로 시작(부팅도 한 번 더 확인한다)
    return { ok: true, value: local };
  } catch (err) {
    const f = failure(err);
    // 확실히 거절된 요청(4xx)은 대기 id를 지운다. 오프라인·5xx는 서버에 생겼을 수 있어 남긴다
    if (f.code !== 'offline') updateRoster(kv, clearPending);
    return { ok: false, failure: f };
  }
}

/** 캐릭터 범위 영웅 선택 키가 없으면 직업으로 채운다(없으면 HeroSelectStore가 카엘로 시작 — Codex P2 #214). 플레이 가능 영웅만. 생성 직후·부팅 때 */
function initHeroSelect(kv: KV, id: string, cls: string | undefined): void {
  try {
    const view = new CharacterStorage(kv, id);
    if (cls && (PLAYABLE_HEROES as readonly string[]).includes(cls) && view.getItem(HERO_SELECT_KEY) === null) view.setItem(HERO_SELECT_KEY, JSON.stringify({ version: 1, hero: cls }));
  } catch { /* 다음 부팅에 다시 */ }
}

/** 지금 캐릭터로 정한다(저장만 — 호출자가 게임 저장 flush 뒤 새로고침해야 적용된다). 목록에 없거나 삭제 유예 중이면 false */
export function selectCharacter(id: string, kv: KV | null = browserStorage()): boolean {
  if (!kv) return false;
  const r = rosterOf(kv);
  const c = r?.characters.find((x) => x.id === id);
  if (!r || !c || c.deletedAt || c.needsDownload) return false;
  return saveRoster(kv, { ...r, current: id });
}

/** 이름 정하기(이관 캐릭터 첫 접속)·바꾸기(1회 무료) */
export async function renameCharacter(id: string, name: string, kv: KV | null = browserStorage()): Promise<CharacterOutcome<LocalCharacter>> {
  if (!kv) return { ok: false, failure: { code: 'storage' } };
  const c = checkName(name);
  if (!c.ok) return { ok: false, failure: { code: 'bad-name', reason: c.reason } };
  try {
    const res = await charApi().rename(id, c.name);
    const local = fromView(res.character);
    updateRoster(kv, (r) => ({ ...r, characters: r.characters.map((x) => (x.id === id ? { ...x, ...local } : x)) }));
    return { ok: true, value: local };
  } catch (err) { return { ok: false, failure: failure(err) }; }
}

/** 삭제(confirm = 지금 이름, 이름이 없으면 'delete'). Lv30+는 7일 유예(purgeAt). 지금 캐릭터였으면 선택을 비운다 */
export async function deleteCharacter(id: string, confirm: string, kv: KV | null = browserStorage()): Promise<CharacterOutcome<{ purgeAt: number | null }>> {
  if (!kv) return { ok: false, failure: { code: 'storage' } };
  try {
    const res = await charApi().remove(id, confirm);
    updateRoster(kv, (r) => ({
      ...r, current: r.current === id ? null : r.current,
      characters: res.purgeAt === null ? r.characters.filter((x) => x.id !== id) : r.characters.map((x) => (x.id === id ? { ...x, deletedAt: Date.now(), purgeAt: res.purgeAt } : x)),
    }));
    // 영구 삭제여도 로컬 진행 키는 남긴다(되돌릴 수 없는 로컬 삭제는 하지 않는다 — 서버 행이 없으니 다시 동기화되지 않는다)
    return { ok: true, value: { purgeAt: res.purgeAt } };
  } catch (err) { return { ok: false, failure: failure(err) }; }
}

/** 삭제 유예 중 복구 */
export async function restoreCharacter(id: string, kv: KV | null = browserStorage()): Promise<CharacterOutcome<LocalCharacter>> {
  if (!kv) return { ok: false, failure: { code: 'storage' } };
  try {
    const res = await charApi().restore(id);
    const local = fromView(res.character);
    updateRoster(kv, (r) => ({ ...r, characters: r.characters.map((x) => (x.id === id ? { ...x, ...local, deletedAt: null, purgeAt: null } : x)) }));
    return { ok: true, value: local };
  } catch (err) { return { ok: false, failure: failure(err) }; }
}

/**
 * 계정 클라이언트용: 지금 캐릭터가 이 계정에 등록됐는지 확인하고, 아니면(이관·오프라인 생성·기기 계정 전환) 등록한다. 등록되면 true.
 * 같은 계정의 같은 id는 서버가 멱등으로 돌려주고, 다른 계정 id면 409 id-taken → conflict 표시 후 TransportError(동기화 멈춤, 사용자 확인).
 */
export async function ensureRegistered(kv: KV, id: string, a: CharacterApi = charApi(), accountId?: string): Promise<boolean> {
  const r = rosterOf(kv);
  const c = r?.characters.find((x) => x.id === id);
  if (!r || !c) return false;
  if (accountId && c.conflictAccounts?.includes(accountId)) throw new TransportError(409, 'id-taken');
  if (c.registered && (accountId === undefined || c.account === accountId)) return true;
  // 칸이 모자라 이 기기에만 보관 중이면 다시 두드리지 않는다(목록을 맞출 때 칸이 생기면 표시가 지워진다)
  if (c.localOnly) throw new TransportError(409, 'slots-full');
  try {
    const res = await a.create({ id: c.id, cls: c.cls, look: c.look, name: c.name, origin: c.origin });
    const v = fromView(res.character, accountId);
    // 기다리는 사이 바뀐 목록을 덮지 않게 지금 목록을 다시 읽어 이 줄만 바꾼다. 소유가 확인됐으니 이 계정의 충돌 표시는 지운다
    updateRoster(kv, (now) => ({ ...now, characters: now.characters.map((x) => (x.id === id ? { ...x, ...v, conflictAccounts: undefined } : x)) }));
    return true;
  } catch (err) {
    if (err instanceof TransportError && err.code === 'slots-full') {
      // 이 계정에 칸이 모자람: 이 캐릭터는 이 기기에만 보관(진행은 로컬 그대로 — 칸이 생기면 다시 올린다)
      updateRoster(kv, (now) => ({ ...now, characters: now.characters.map((x) => (x.id === id ? { ...x, localOnly: true } : x)) }));
    }
    if (err instanceof TransportError && err.code === 'id-taken' && accountId) {
      updateRoster(kv, (now) => ({ ...now, characters: now.characters.map((x) => (x.id === id ? { ...x, conflictAccounts: [...new Set([...(x.conflictAccounts ?? []), accountId])] } : x)) }));
    }
    throw err;
  }
}

/**
 * 계정 삭제 + 기기 진행 보존: 로컬 캐릭터를 "보존된 로컬 캐릭터"로 바꾼다(origin 'retained', 등록·소유 표시 지움). 새 계정에서 일반 새 캐릭터 등록 경로로 올라간다.
 * 이관 표시는 "이 기기 원래 키는 이미 캐릭터로 옮겨짐(보존)"으로 남겨 다시 이관하지 않는다(보존 캐릭터와 원래 키 진행이 두 번 생기지 않게 — Codex P2 #214)
 */
export function retainLocalCharacters(kv: KV | null = browserStorage()): boolean {
  if (!kv) return false;
  return updateRoster(kv, (r) => {
    const { pendingMigration: _p, ...rest } = r;
    return {
      ...rest,
      characters: r.characters.filter((c) => !c.deletedAt).map((c) => ({ id: c.id, cls: c.cls, look: c.look, slot: c.slot, name: c.name, tag: c.tag, origin: 'retained', registered: false })),
      migrated: r.migrated ? { ...r.migrated, account: '', mode: r.migrated.mode === 'none' ? 'none' : 'adopt' } : r.migrated,
    };
  });
}

let transportFor: (id: string) => Transport = (id) => characterTransport(id);
/** 시험·QA용 주입 */
export function setCharacterTransport(f: ((id: string) => Transport) | null): void { transportFor = f ?? ((id) => characterTransport(id)); }
function accountOf(kv: KV): string | null {
  try { const m = JSON.parse(kv.getItem(ACCOUNT_META_KEY) ?? 'null') as { accountId?: unknown } | null; return m && typeof m.accountId === 'string' ? m.accountId : null; } catch { return null; }
}
/**
 * 처음 보는 서버 캐릭터(needsDownload) 받기: 스냅샷을 내려받아 캐릭터 키에 쓰고 동기화 기준(rev)을 적은 뒤에만 선택할 수 있게 한다.
 * 실패한 캐릭터는 표시를 남겨 다음에 다시. 받은 수
 */
export async function hydrateCharacters(kv: KV, accountId: string | null): Promise<number> {
  if (!accountId) return 0;
  let n = 0;
  for (const c of listCharacters(kv).filter((x) => x.needsDownload)) {
    try {
      const s = await transportFor(c.id).getSave(false);
      const data = s.data ?? EMPTY_SERVER_SNAPSHOT;
      // 설치·검증(다시 읽어 해시 비교) 뒤에만 기준을 적는다 — 그러면 deriveRosterState가 「받는 중」을 푼다. 실패하면 다음에 다시
      if (!installVerified(kv, c.id, accountId, s.rev, data, () => writeSnapshot(new CharacterStorage(kv, c.id), data), Date.now())) continue;
      updateRoster(kv, (r) => ({ ...r, characters: r.characters.map((x) => (x.id === c.id ? { ...x, serverRev: s.rev } : x)) }));
      n++;
    } catch { /* 다음에 다시 */ }
  }
  return n;
}

/** 캐릭터 레벨(이 기기에 있는 그 캐릭터 진행 — 성장 장부를 저장 모듈과 같은 규칙으로 읽는다). 받지 않은 캐릭터·읽기 실패는 1 */
export function characterLevel(c: Pick<LocalCharacter, 'id' | 'cls'>, kv: KV | null = browserStorage()): number {
  if (!kv) return 1;
  try { const l = legacyLevels(new CharacterStorage(kv, c.id)); return Math.max(1, c.cls === 'lyra' ? l.lyra : l.kael); } catch { return 1; }
}

/** 이 기기에만 보관 중인 캐릭터 수(계정에 칸이 모자라 서버에 못 올림) — 안내 「칸이 부족해 n개는 이 기기에만 보관됩니다」 */
export function localOnlyCount(kv: KV | null = browserStorage()): number { return listCharacters(kv).filter((c) => c.localOnly).length; }
